import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { formatDate } from "../../lib/date";
import { Plus, Pencil, Trash2, Loader2, RefreshCw, Target, Compass, Crosshair } from "lucide-react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KIND_META = {
  strategy: { label: "Strategy", icon: Compass, iconBg: "#EAF1FF", iconFg: "#1D4ED8", blurb: "The plays that will get EQUIPR where it's going — pillars and initiatives.", noun: "initiative" },
  goals: { label: "Goals", icon: Target, iconBg: "#E9F6EE", iconFg: "#0E6B3F", blurb: "Measurable outcomes the team is driving toward, with owners and target dates.", noun: "goal" },
  focus: { label: "Focus", icon: Crosshair, iconBg: "#FFF8D6", iconFg: "#8A6D00", blurb: "What we're actively concentrating on right now — the current priorities.", noun: "focus area" },
};

const STATUS_META = {
  planned: { label: "Planned", tone: "neutral" },
  active: { label: "Active", tone: "success" },
  on_hold: { label: "On hold", tone: "warning" },
  done: { label: "Done", tone: "neutral" },
};
const STATUS_ORDER = ["planned", "active", "on_hold", "done"];

const emptyForm = { title: "", description: "", owner: "", status: "active", target_date: "", progress: "" };

export default function CompanyList({ kind = "goals" }) {
  const meta = KIND_META[kind] || KIND_META.goals;
  const Icon = meta.icon;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reps, setReps] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/company/items`, { params: { kind } });
      setItems(r.data.items || []);
    } catch (e) {
      toast.error(`Failed to load ${meta.label.toLowerCase()}`);
    } finally {
      setLoading(false);
    }
  }, [kind, meta.label]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    axios.get(`${API}/agents/mapping`)
      .then(r => setReps((r.data.agents || []).filter(a => a.id !== "emergency").map(a => a.name).filter(Boolean)))
      .catch(() => {});
  }, []);

  const openAdd = () => { setEditing(null); setForm({ ...emptyForm }); setDialogOpen(true); };
  const openEdit = (it) => {
    setEditing(it.id);
    setForm({
      title: it.title || "", description: it.description || "", owner: it.owner || "",
      status: it.status || "active", target_date: it.target_date || "",
      progress: (it.progress ?? "") === null ? "" : String(it.progress ?? ""),
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        description: form.description || "",
        owner: form.owner || null,
        status: form.status,
        target_date: form.target_date || null,
        progress: form.progress === "" ? null : parseInt(form.progress, 10),
      };
      if (editing) {
        await axios.put(`${API}/company/items/${editing}`, payload);
        toast.success(`${meta.label} updated`);
      } else {
        await axios.post(`${API}/company/items`, { kind, ...payload });
        toast.success(`${meta.noun[0].toUpperCase() + meta.noun.slice(1)} added`);
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/company/items/${deleteTarget.id}`);
      toast.success("Removed");
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error("Failed to remove");
    } finally {
      setDeleting(false);
    }
  };

  const showProgress = kind === "goals";

  return (
    <div className="space-y-4" data-testid={`company-${kind}-view`}>
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: meta.iconBg }}>
            <Icon className="w-5 h-5" style={{ color: meta.iconFg }} />
          </div>
          <div>
            <h2 className="heading text-2xl text-[#1A1A1A] tracking-tight">{meta.label}</h2>
            <p className="text-sm text-[#59554D]">{meta.blurb}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={load} disabled={loading}
            className="h-9 border-[#DAD5CA] bg-transparent text-[#59554D] hover:bg-[#F4F2ED] uppercase text-[11px] tracking-wide font-semibold" data-testid={`company-${kind}-refresh`}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
          <Button onClick={openAdd}
            className="h-9 bg-[#FFD108] hover:bg-[#E0B800] text-[#1A1A1A] uppercase text-[11px] tracking-wide font-semibold" data-testid={`company-${kind}-add`}>
            <Plus className="w-4 h-4 mr-2" /> Add {meta.label === "Focus" ? "focus" : meta.noun}
          </Button>
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="grid gap-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <Card key={i} className="bg-white border-[#DAD5CA] rounded-xl"><CardContent className="p-4"><div className="skeleton h-5 w-1/3 mb-2" /><div className="skeleton h-3.5 w-2/3" /></CardContent></Card>
          ))}
        </div>
      ) : items.length === 0 ? (
        <Card className="bg-white border-[#DAD5CA] rounded-xl"><CardContent className="px-6 py-12 text-center">
          <Icon className="w-8 h-8 mx-auto mb-2" style={{ color: meta.iconFg }} />
          <p className="text-[15px] font-semibold text-[#1A1A1A]">No {meta.label.toLowerCase()} yet</p>
          <p className="text-sm text-[#59554D] mt-1">Add the first {meta.noun} to get the team aligned.</p>
          <Button onClick={openAdd} className="mt-4 bg-[#FFD108] hover:bg-[#E0B800] text-[#1A1A1A]" data-testid={`company-${kind}-add-empty`}><Plus className="w-4 h-4 mr-2" /> Add {meta.noun}</Button>
        </CardContent></Card>
      ) : (
        <div className="grid gap-3">
          {items.map((it) => {
            const st = STATUS_META[it.status] || STATUS_META.active;
            return (
              <Card key={it.id} className="bg-white border-[#DAD5CA] rounded-xl shadow-card group" data-testid={`company-${kind}-item-${it.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[15px] font-semibold text-[#1A1A1A]">{it.title}</span>
                        <span className={`ds-pill ${st.tone}`} data-testid={`company-${kind}-status-${it.id}`}><span className="dot" /> {st.label}</span>
                      </div>
                      {it.description && <p className="text-sm text-[#59554D] mt-1.5 whitespace-pre-wrap">{it.description}</p>}
                      <div className="flex items-center gap-4 flex-wrap mt-2 text-[12px] text-[#8A857A]">
                        {it.owner && <span className="text-[#59554D]">Owner: <span className="font-medium text-[#1A1A1A]">{it.owner}</span></span>}
                        {it.target_date && <span>Target: <span className="mono text-[#59554D]">{formatDate(it.target_date)}</span></span>}
                      </div>
                      {showProgress && (it.progress || it.progress === 0) && (
                        <div className="mt-2.5 flex items-center gap-2 max-w-[320px]">
                          <div className="flex-1 h-2 rounded-full bg-[#F0EDE6] overflow-hidden">
                            <div className="h-full rounded-full bg-[#0E6B3F] transition-all" style={{ width: `${it.progress}%` }} />
                          </div>
                          <span className="mono text-[11px] text-[#59554D] w-9 text-right">{it.progress}%</span>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-0.5 shrink-0">
                      <button type="button" title="Edit" onClick={() => openEdit(it)}
                        className="w-8 h-8 rounded-md flex items-center justify-center text-[#6B6660] hover:bg-[#F4F2ED] hover:text-[#1A1A1A]" data-testid={`company-${kind}-edit-${it.id}`}>
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button type="button" title="Remove" onClick={() => setDeleteTarget(it)}
                        className="w-8 h-8 rounded-md flex items-center justify-center text-[#C23A00] hover:bg-[#FFE9E0]" data-testid={`company-${kind}-delete-${it.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Add / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={(o) => { if (!o && !saving) setDialogOpen(false); }}>
        <DialogContent className="bg-white border-[#DAD5CA] text-[#1A1A1A] max-w-lg" data-testid={`company-${kind}-dialog`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Icon className="w-4 h-4" style={{ color: meta.iconFg }} /> {editing ? "Edit" : "Add"} {meta.noun}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs text-[#59554D]">Title *</label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder={kind === "goals" ? "e.g. Reach 150 active clients" : kind === "strategy" ? "e.g. Expand into civil sector" : "e.g. Ship the Quotes MVP"}
                className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]" data-testid={`company-${kind}-field-title`} />
            </div>
            <div>
              <label className="text-xs text-[#59554D]">Description</label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="What does success look like? Any context…"
                className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A] min-h-[90px]" data-testid={`company-${kind}-field-desc`} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-[#59554D]">Owner</label>
                <Select value={form.owner || "none"} onValueChange={(v) => setForm({ ...form, owner: v === "none" ? "" : v })}>
                  <SelectTrigger className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]" data-testid={`company-${kind}-field-owner`}><SelectValue placeholder="Unassigned" /></SelectTrigger>
                  <SelectContent className="bg-[#F4F2ED] border-[#DAD5CA]">
                    <SelectItem value="none">Unassigned</SelectItem>
                    {reps.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-[#59554D]">Status</label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]" data-testid={`company-${kind}-field-status`}><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#F4F2ED] border-[#DAD5CA]">
                    {STATUS_ORDER.map((s) => <SelectItem key={s} value={s}>{STATUS_META[s].label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-[#59554D]">Target date</label>
                <Input type="date" value={form.target_date} onChange={(e) => setForm({ ...form, target_date: e.target.value })}
                  className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]" data-testid={`company-${kind}-field-target`} />
              </div>
              {showProgress && (
                <div>
                  <label className="text-xs text-[#59554D]">Progress (%)</label>
                  <Input type="number" min="0" max="100" value={form.progress} onChange={(e) => setForm({ ...form, progress: e.target.value })}
                    placeholder="0–100" className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]" data-testid={`company-${kind}-field-progress`} />
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogOpen(false)} className="text-[#59554D]">Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-[#FFD108] hover:bg-[#E0B800] text-[#1A1A1A] font-medium" data-testid={`company-${kind}-save`}>
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-1" />} {editing ? "Save changes" : "Add"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => { if (!o && !deleting) setDeleteTarget(null); }}>
        <DialogContent className="bg-white border-[#DAD5CA] text-[#1A1A1A] max-w-sm" data-testid={`company-${kind}-delete-dialog`}>
          <DialogHeader><DialogTitle>Remove this {meta.noun}?</DialogTitle></DialogHeader>
          <p className="text-sm text-[#59554D]">"{deleteTarget?.title}" will be permanently removed.</p>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleteTarget(null)} className="text-[#59554D]">Cancel</Button>
            <Button onClick={remove} disabled={deleting} className="bg-[#C23A00] hover:bg-[#A83200] text-white" data-testid={`company-${kind}-delete-confirm`}>
              {deleting && <Loader2 className="w-4 h-4 animate-spin mr-1" />} Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
